import { type EditorWidget } from 'mini-code'
import type React from 'react'
import {
  type TimelineInfo,
} from '../bytecode.ts'
import type { TimelinePosition } from '../ui-timeline.ts'

export class TimelineWidget {
  private widgetKey: string
  private opacity = 0
  private contextId: string = ''
  private _info: TimelineInfo

  constructor(
    info: TimelineInfo,
    private timelinePositionsRef: React.RefObject<Map<number, TimelinePosition>>,
    private widgetOpacityRef?: React.RefObject<Map<string, number>>,
  ) {
    this._info = info
    this.widgetKey = `timeline-${info.id}-${info.line}-${info.column}`
  }

  get info(): TimelineInfo {
    return this._info
  }

  setInfo(info: TimelineInfo): void {
    this._info = info
    this.widgetKey = `timeline-${info.id}-${info.line}-${info.column}`
  }

  setContextId(contextId: string): void {
    this.contextId = contextId
  }

  toEditorWidget(): EditorWidget {
    const widget = {
      type: 'overlay' as const,
      line: this._info.line,
      column: this._info.column,
      length: this._info.length,

      render: (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) => {
        // Use current info (may have been updated via setInfo)
        const currentInfo = this._info

        if (this.widgetOpacityRef) {
          this.opacity = this.widgetOpacityRef.current?.get(this.widgetKey) ?? 0
        }

        const position = this.timelinePositionsRef.current?.get(currentInfo.id)

        // Determine if this character is currently active
        // position.segmentIndex is the parsed segment index (0, 1, 2...)
        // currentInfo.segmentIndex is also the parsed segment index (matches position.segmentIndex)
        if (position !== undefined && currentInfo.segmentIndex !== undefined) {
          const currentSegmentIndex = position.segmentIndex
          const currentSegment = position.currentSegment

          // For rise/fall: segmentIndex matches directly
          // For hold: segmentIndex matches, and we need to check which character within the segment
          let isActive = false

          if (currentSegmentIndex === currentInfo.segmentIndex) {
            if (currentSegment && currentSegment.type === 'hold') {
              // For hold segments, calculate which character should be active
              const positionInSegment = position.positionInSegment
              const segmentDivisions = currentSegment.divisions
              const divisionIndex = Math.floor(positionInSegment * segmentDivisions)

              // Calculate which character index within this segment this widget represents
              // We need to know the characterIndex offset for this segment
              // For now, let's just highlight all characters in the active hold segment
              // (we can refine this later to highlight only the active division)
              isActive = true
            }
            else {
              // For rise/fall, segmentIndex match means this character is active
              isActive = true
            }
          }

          if (isActive) {
            this.opacity = 1
          }
          else {
            this.opacity *= 0.6
            if (this.opacity < 0.01) this.opacity = 0
          }
        }
        else {
          this.opacity *= 0.6
          if (this.opacity < 0.01) this.opacity = 0
        }

        if (this.widgetOpacityRef) {
          this.widgetOpacityRef.current?.set(this.widgetKey, this.opacity)
        }

        if (this.opacity) {
          // Color based on segment type
          let color = 'rgba(100,100,250,' // default blue
          if (currentInfo.segmentType === 'rise') {
            color = 'rgba(50,200,100,' // green for rise
          }
          else if (currentInfo.segmentType === 'fall') {
            color = 'rgba(250,100,100,' // red for fall
          }
          else if (currentInfo.segmentType === 'hold') {
            color = currentInfo.segmentValue === 1 ? 'rgba(100,150,250,' : 'rgba(100,100,150,' // bright for high, dim for low
          }

          ctx.strokeStyle = `${color}${this.opacity ** 0.3})`
          ctx.lineWidth = 1
          ctx.strokeRect(x, y - 1, width, height)
        }
      },
    }
    return widget
  }
}
